import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

// ── Per-weapon sound profiles ─────────────────────────────────────────
// Each weapon maps to its firing / reload / deploy audio. A weapon with
// no entry here stays silent. Adding a profile is all it takes to "light
// up" a weapon — assets are discovered + loaded from the URLs below.
interface ReloadStep {
  url: string;
  /** Seconds after the reload starts to play this step. */
  at: number;
}
interface WeaponSoundProfile {
  /** Firing sound (long-range, pitch-jittered). */
  shot: string;
  /** Distinct final-round sound; falls back to `shot` when absent. */
  lastShot?: string;
  /** Single-file reload (pistol-style). */
  reload?: string;
  /** Multi-stage reload (e.g. clipout → clipin → boltpull). */
  reloadSeq?: ReloadStep[];
  /** Deploy/"draw" sound played when this weapon is equipped. */
  draw?: string;
  /** Scope toggle sound (snipers), played locally on each zoom change. */
  zoom?: string;
}

// Both M4 rifles share one reload — mag out, fresh mag in, mag seat
// (cliphit), then the charging-handle rack (back → forward) — timed under
// the 3.1 s reload animation.
const M4_RELOAD_SEQ: ReloadStep[] = [
  { url: "/audio/m4-clipout.mp3", at: 0.3 },
  { url: "/audio/m4-clipin.mp3", at: 1.3 },
  { url: "/audio/m4-cliphit.mp3", at: 1.7 },
  { url: "/audio/m4-boltback.mp3", at: 2.2 },
  { url: "/audio/m4-boltforward.mp3", at: 2.5 },
];

const PROFILES: Record<string, WeaponSoundProfile> = {
  // Terrorist starting pistol (G-18, unsilenced).
  g18: {
    shot: "/audio/pistol-shot.mp3",
    lastShot: "/audio/pistol-last-shot.mp3",
    reload: "/audio/pistol-reload.mp3",
    draw: "/audio/glock-draw.mp3",
  },
  // USP-S — CT starter pistol (suppressed). Three-stage reload (mag out,
  // fresh mag in, then the slide release), timed under the 2.2 s reload
  // animation. Draw plays when swapping onto the pistol.
  usp_s: {
    shot: "/audio/usp-shot.mp3",
    draw: "/audio/usp-draw.mp3",
    reloadSeq: [
      { url: "/audio/usp-clipout.mp3", at: 0.25 },
      { url: "/audio/usp-clipin.mp3", at: 1.1 },
      { url: "/audio/usp-slideback.mp3", at: 1.8 },
    ],
  },
  // Desert Eagle — hand cannon. Booming fire + draw, plus a four-stage
  // reload timed under the 2.2 s reload animation: mag out, fresh mag in,
  // then the slide cycle (back → forward).
  deagle: {
    shot: "/audio/deagle-shot.mp3",
    draw: "/audio/deagle-draw.mp3",
    reloadSeq: [
      { url: "/audio/deagle-clipout.mp3", at: 0.25 },
      { url: "/audio/deagle-clipin.mp3", at: 1.1 },
      { url: "/audio/deagle-slideback.mp3", at: 1.5 },
      { url: "/audio/deagle-slideforward.mp3", at: 1.7 },
    ],
  },
  // Dual Berettas — twin pistols, no dedicated CS:GO pack available, so we
  // borrow: the P250 report for fire and the G-18 reload for the mag swap,
  // and the glock draw for the deploy. The reload plays *twice*, staggered,
  // under the long 4.5 s two-gun reload so you hear both pistols worked —
  // the second play uses its own slot (-b copy) so it layers over the first
  // instead of cutting it off (the mechanical emitter doesn't self-overlap).
  dualies: {
    shot: "/audio/dualies-shot.mp3",
    draw: "/audio/glock-draw.mp3",
    reloadSeq: [
      { url: "/audio/pistol-reload.mp3", at: 0.1 },
      { url: "/audio/dualies-reload-b.mp3", at: 1.7 },
    ],
  },
  // AWP — bolt-action sniper. Fire + draw + scope-toggle (zoom), plus a
  // five-stage reload timed under the 3.7 s reload animation: mag out,
  // fresh mag in, mag seat (cliphit), then the bolt cycle (back → forward).
  awp: {
    shot: "/audio/awp-shot.mp3",
    draw: "/audio/awp-draw.mp3",
    zoom: "/audio/awp-zoom.mp3",
    reloadSeq: [
      { url: "/audio/awp-clipout.mp3", at: 0.35 },
      { url: "/audio/awp-clipin.mp3", at: 1.85 },
      { url: "/audio/awp-cliphit.mp3", at: 2.15 },
      { url: "/audio/awp-boltback.mp3", at: 2.45 },
      { url: "/audio/awp-boltforward.mp3", at: 2.75 },
    ],
  },
  // AK-47 — CS-style fire + three-stage reload, timed to land under the
  // 2.5 s reload animation: mag out, fresh mag in, then the charging-
  // handle rack. Draw plays when swapping onto the rifle.
  ak47: {
    shot: "/audio/ak47-shot.mp3",
    draw: "/audio/ak47-draw.mp3",
    reloadSeq: [
      { url: "/audio/ak47-clipout.mp3", at: 0.25 },
      { url: "/audio/ak47-clipin.mp3", at: 1.25 },
      { url: "/audio/ak47-boltpull.mp3", at: 1.7 },
    ],
  },
  // M4A4 — CT rifle, unsilenced fire. Shared M4 five-stage reload; reuses
  // the AK-47 draw sound for the deploy.
  m4a4: {
    shot: "/audio/m4a4-shot.mp3",
    draw: "/audio/ak47-draw.mp3",
    reloadSeq: M4_RELOAD_SEQ,
  },
  // M4A1-S — CT rifle, suppressed fire (its own quieter shot). Same reload
  // and the same AK-47 draw sound.
  m4a1_s: {
    shot: "/audio/m4a1s-shot.mp3",
    draw: "/audio/ak47-draw.mp3",
    reloadSeq: M4_RELOAD_SEQ,
  },
  // MAC-10 — T-side full-auto SMG. Fire + draw, plus a four-stage reload
  // timed under the 2.4 s reload animation: mag out, fresh mag in, then the
  // bolt cycle (back → forward).
  mac10: {
    shot: "/audio/mac10-shot.mp3",
    draw: "/audio/mac10-draw.mp3",
    reloadSeq: [
      { url: "/audio/mac10-clipout.mp3", at: 0.3 },
      { url: "/audio/mac10-clipin.mp3", at: 1.1 },
      { url: "/audio/mac10-boltback.mp3", at: 1.6 },
      { url: "/audio/mac10-boltforward.mp3", at: 2.1 },
    ],
  },
  // MP9 — CT-side full-auto SMG. Fire + draw, plus a four-stage reload timed
  // under the 2.1 s reload animation: mag out, fresh mag in, then the bolt
  // cycle (back → forward).
  mp9: {
    shot: "/audio/mp9-shot.mp3",
    draw: "/audio/mp9-draw.mp3",
    reloadSeq: [
      { url: "/audio/mp9-clipout.mp3", at: 0.25 },
      { url: "/audio/mp9-clipin.mp3", at: 1.0 },
      { url: "/audio/mp9-boltback.mp3", at: 1.45 },
      { url: "/audio/mp9-boltforward.mp3", at: 1.75 },
    ],
  },
};

/** Weapons that currently have audio — derived from the profile registry. */
const AUDIBLE_WEAPONS = new Set<string>(Object.keys(PROFILES));

// ── URL → role index (built once from the profiles above) ─────────────
// "fire" sounds carry across the map on the loud gunfire emitter; "reload",
// "draw" and "zoom" are quiet mechanical sounds on a short-range emitter.
type Role = "fire" | "reload" | "draw" | "zoom";
const ROLE_BY_URL = new Map<string, Role>();
for (const prof of Object.values(PROFILES)) {
  ROLE_BY_URL.set(prof.shot, "fire");
  if (prof.lastShot) ROLE_BY_URL.set(prof.lastShot, "fire");
  if (prof.reload) ROLE_BY_URL.set(prof.reload, "reload");
  for (const step of prof.reloadSeq ?? []) ROLE_BY_URL.set(step.url, "reload");
  if (prof.draw) ROLE_BY_URL.set(prof.draw, "draw");
  if (prof.zoom) ROLE_BY_URL.set(prof.zoom, "zoom");
}
const ALL_URLS = Array.from(ROLE_BY_URL.keys());
/** Stable, collision-free slot name for a sound URL. */
const slotName = (url: string): string => url.replace(/[^a-z0-9]/gi, "_");

// ── Positional tuning ─────────────────────────────────────────────────
// Gunfire carries across the arena with a punchy near/far contrast, so it
// uses an inverse-distance curve (volume roughly halves every ~refDistance
// metres past the first). A small per-shot pitch jitter keeps repeated
// fire from sounding mechanical.
const FIRE_REF_DISTANCE = 4; // full volume within 4 m, then falls off fast
const FIRE_MAX_DISTANCE = 80; // far shots stay faint, never fully silent
const FIRE_ROLLOFF = 1.1;
const FIRE_PEER_VOLUME = 0.9;
const FIRE_PITCH_JITTER = 0.06; // ±6 % random pitch per shot

// Reload + draw are quiet mechanical sounds on a short *linear* curve — a
// nearby enemy catches them but they're gone before mid-range, and never
// loud even point-blank.
const RELOAD_REF_DISTANCE = 2;
const RELOAD_MAX_DISTANCE = 16; // fully silent past 16 m
const RELOAD_ROLLOFF = 1.0;
const RELOAD_PEER_VOLUME = 0.3; // point-blank ceiling — deliberately low
const DRAW_PEER_VOLUME = 0.35; // deploy is a touch more present than reload

const LOCAL_SHOT_VOLUME = 0.5;
const LOCAL_RELOAD_VOLUME = 0.4;
const LOCAL_DRAW_VOLUME = 0.45;
const LOCAL_ZOOM_VOLUME = 0.5; // own scope click — a clear, present blip

function peerVolume(role: Role): number {
  if (role === "fire") return FIRE_PEER_VOLUME;
  if (role === "draw") return DRAW_PEER_VOLUME;
  return RELOAD_PEER_VOLUME; // reload + zoom share the quiet mechanical ceiling
}
function localVolume(role: Role): number {
  if (role === "fire") return LOCAL_SHOT_VOLUME;
  if (role === "draw") return LOCAL_DRAW_VOLUME;
  if (role === "zoom") return LOCAL_ZOOM_VOLUME;
  return LOCAL_RELOAD_VOLUME;
}

interface PeerEmitters {
  /** Long-range gunfire emitter (fire-role slots). */
  fire: pc.Entity;
  /** Short-range, quiet emitter (reload + draw + zoom slots). */
  reload: pc.Entity;
}

/**
 * WeaponSounds — gunfire, reload + weapon-draw audio.
 *
 * The local player hears their own shot the instant they fire
 * (client-predicted via `onLocalFire`, no server round-trip) at a fixed
 * 2D volume. Every other player's shots are 3D positional via the shared
 * AudioListener on the camera + a per-peer emitter placed at the shot
 * origin — so an opponent's fire swells as they close in and fades with
 * distance. The final round in a magazine plays a distinct "last shot"
 * sound when the weapon defines one.
 *
 * Reloads + weapon draws are detected from the authoritative snapshot
 * (`reloading` flag and `weaponId` changes) and play locally (2D) or
 * positionally (peers) on a separate, much quieter and shorter-range
 * emitter, since they're subtle mechanical sounds an enemy should only
 * hear up close, not ones that carry across the map. A weapon may define
 * a single reload clip (pistol) or a timed multi-stage sequence — clip
 * out, clip in, bolt pull — that lands under its reload animation.
 */
export class WeaponSounds {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly localEmitter: pc.Entity;
  private readonly peers = new Map<string, PeerEmitters>();
  /** Last-seen reload flag per session, to fire the sound once on start. */
  private readonly lastReloading = new Map<string, boolean>();
  /** Last-seen weapon per session, to play the draw sound once on switch. */
  private readonly lastWeaponId = new Map<string, string>();
  /** Pending multi-stage reload timers per session (cleared on re-reload). */
  private readonly reloadTimers = new Map<string, number[]>();
  /** Predicted magazine count per session — drives "last shot" detection
   *  without depending on the lagged snapshot mid-burst. */
  private readonly predMag = new Map<string, number>();
  /** Loaded audio assets keyed by source URL. */
  private readonly assets = new Map<string, pc.Asset>();
  private ready = false;
  private readonly unsubs: Array<() => void> = [];
  private readonly tickFn: () => void;

  constructor(app: pc.AppBase, client: GameClient, camera: pc.Entity) {
    this.app = app;
    this.client = client;

    // Positional peer audio needs a listener; the camera is the ears.
    // Idempotent — Footsteps may have already added it.
    if (!camera.audiolistener) camera.addComponent("audiolistener");

    // Local gunfire/reload/draw — 2D (fixed volume), parented to the camera.
    this.localEmitter = new pc.Entity("weapon-snd-local");
    camera.addChild(this.localEmitter);

    this.loadAssets();
    unlockAudioOnGesture(this.app);

    // Local shot — immediate, client-predicted (matches the muzzle flash).
    this.unsubs.push(
      this.client.onLocalFire(() => {
        const self = this.client.getLatestSelf();
        if (!self) return;
        const prof = PROFILES[self.weaponId];
        if (!prof) return;
        const last = this.consumeShot(self.sessionId, self.magAmmo ?? 0);
        this.playLocal(last && prof.lastShot ? prof.lastShot : prof.shot);
      }),
    );

    // Peer shots — positional. Skip self (handled immediately above).
    this.unsubs.push(
      this.client.onPeerFire((msg) => {
        if (msg.attackerSessionId === this.client.sessionId) return;
        const prof = PROFILES[msg.weaponId];
        if (!prof) return;
        const snap = this.client.getPlayerSnapshot(msg.attackerSessionId);
        const last = this.consumeShot(msg.attackerSessionId, snap?.magAmmo ?? 0);
        const url = last && prof.lastShot ? prof.lastShot : prof.shot;
        this.playPeer(msg.attackerSessionId, url, msg.ox, msg.oy, msg.oz);
      }),
    );

    this.tickFn = () => this.update();
    this.app.on("update", this.tickFn);
  }

  destroy(): void {
    this.app.off("update", this.tickFn);
    for (const u of this.unsubs) u();
    for (const timers of this.reloadTimers.values()) for (const t of timers) clearTimeout(t);
    this.reloadTimers.clear();
    for (const e of this.peers.values()) {
      e.fire.destroy();
      e.reload.destroy();
    }
    this.peers.clear();
    this.localEmitter.destroy();
  }

  // ── Reload + draw detection, emitter pruning ────────────────────────
  private update(): void {
    if (!this.ready) return;
    const selfId = this.client.sessionId;
    const seen = new Set<string>();

    this.client.forEachKnownPlayer((p) => {
      seen.add(p.sessionId);
      const isSelf = p.sessionId === selfId;
      const prof = PROFILES[p.weaponId];

      // Weapon draw — on an actual switch (not first observation / spawn).
      // A switch or drop also aborts a reload in flight, so kill any staged
      // reload clips still pending before they fire on the now-gone weapon.
      const prevW = this.lastWeaponId.get(p.sessionId);
      if (prevW !== undefined && prevW !== p.weaponId) {
        this.cancelReloadSeq(p.sessionId);
        if (prof?.draw) {
          if (isSelf) this.playLocal(prof.draw);
          else this.playPeer(p.sessionId, prof.draw, p.x, p.y + 1.0, p.z);
        }
      }
      this.lastWeaponId.set(p.sessionId, p.weaponId);

      // Reload — fire once on the false→true transition; when it flips back
      // to false early (dropped/switched mid-reload) cancel the staged clips
      // still pending. On a normal finish every step has already fired, so
      // this is a harmless no-op.
      const reloading = !!p.reloading;
      const was = this.lastReloading.get(p.sessionId) ?? false;
      if (reloading && !was && prof) {
        if (prof.reloadSeq) this.scheduleReloadSeq(p.sessionId, isSelf, prof.reloadSeq);
        else if (prof.reload) {
          if (isSelf) this.playLocal(prof.reload);
          else this.playPeer(p.sessionId, prof.reload, p.x, p.y + 1.0, p.z);
        }
      } else if (!reloading && was) {
        this.cancelReloadSeq(p.sessionId);
      }
      this.lastReloading.set(p.sessionId, reloading);
    });

    // Drop emitters + state for players who left.
    for (const [sid, e] of this.peers) {
      if (!seen.has(sid)) {
        e.fire.destroy();
        e.reload.destroy();
        this.peers.delete(sid);
      }
    }
    for (const sid of this.lastReloading.keys()) if (!seen.has(sid)) this.lastReloading.delete(sid);
    for (const sid of this.lastWeaponId.keys()) if (!seen.has(sid)) this.lastWeaponId.delete(sid);
    for (const sid of this.predMag.keys()) if (!seen.has(sid)) this.predMag.delete(sid);
    for (const sid of this.reloadTimers.keys()) {
      if (!seen.has(sid)) {
        for (const t of this.reloadTimers.get(sid)!) clearTimeout(t);
        this.reloadTimers.delete(sid);
      }
    }
  }

  /**
   * Play the local player's scope-toggle sound. Called by the controller
   * each time the sniper zoom level changes. 2D (own weapon) and a no-op
   * for weapons without a zoom clip.
   */
  playZoom(): void {
    const self = this.client.getLatestSelf();
    if (!self) return;
    const prof = PROFILES[self.weaponId];
    if (prof?.zoom) this.playLocal(prof.zoom);
  }

  /**
   * Schedule a multi-stage reload. Each step re-reads the player's live
   * position at fire time so peers stay accurate as they move; the local
   * player's steps play 2D. Any prior pending sequence for the session is
   * cancelled first (a reload restarted mid-animation shouldn't double up).
   */
  private scheduleReloadSeq(sid: string, isSelf: boolean, steps: ReloadStep[]): void {
    this.cancelReloadSeq(sid); // supersede any prior sequence for this session
    const timers: number[] = [];
    for (const step of steps) {
      const handle = window.setTimeout(() => {
        if (isSelf) this.playLocal(step.url);
        else {
          const snap = this.client.getPlayerSnapshot(sid);
          if (snap) this.playPeer(sid, step.url, snap.x, snap.y + 1.0, snap.z);
        }
      }, step.at * 1000);
      timers.push(handle);
    }
    this.reloadTimers.set(sid, timers);
  }

  /**
   * Cancel a session's pending staged reload clips. Called when a reload is
   * aborted before all its steps have fired — the weapon was dropped (G) or
   * switched mid-animation — so the leftover clips don't play on a gun that's
   * no longer equipped. Idempotent, and a no-op once every step has fired.
   */
  private cancelReloadSeq(sid: string): void {
    const timers = this.reloadTimers.get(sid);
    if (!timers) return;
    for (const t of timers) clearTimeout(t);
    this.reloadTimers.delete(sid);
  }

  /**
   * Register one fired round for `sid` and report whether it emptied the
   * magazine. Resyncs to the authoritative count whenever it jumps up
   * (reload refill / first observed shot), then decrements locally so a
   * fast burst isn't fooled by lagged snapshots.
   */
  private consumeShot(sid: string, authMag: number): boolean {
    let cur = this.predMag.get(sid);
    if (cur === undefined || authMag > cur) cur = authMag;
    const last = cur <= 1;
    this.predMag.set(sid, Math.max(0, cur - 1));
    return last;
  }

  // ── Playback ────────────────────────────────────────────────────────
  private playLocal(url: string): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    this.applyJitter(this.localEmitter.sound?.play(slotName(url)), url);
  }

  private playPeer(sid: string, url: string, x: number, y: number, z: number): void {
    if (!this.ready) return;
    const emitters = this.getPeerEmitters(sid);
    const e = ROLE_BY_URL.get(url) === "fire" ? emitters.fire : emitters.reload;
    e.setPosition(x, y, z);
    this.applyJitter(e.sound?.play(slotName(url)), url);
  }

  /** Subtle per-shot pitch variation so repeated gunfire stays alive.
   *  Mechanical sounds (reload/draw) keep their natural pitch. */
  private applyJitter(inst: pc.SoundInstance | null | undefined, url: string): void {
    if (inst && ROLE_BY_URL.get(url) === "fire") {
      inst.pitch = 1 + (Math.random() * 2 - 1) * FIRE_PITCH_JITTER;
    }
  }

  private getPeerEmitters(sid: string): PeerEmitters {
    let emitters = this.peers.get(sid);
    if (!emitters) {
      // Gunfire — inverse-distance so it's punchy up close and dynamic
      // across the map.
      const fire = new pc.Entity(`weapon-fire-${sid}`);
      fire.addComponent("sound");
      const fs = fire.sound;
      if (fs) {
        fs.positional = true;
        fs.distanceModel = pc.DISTANCE_INVERSE;
        fs.refDistance = FIRE_REF_DISTANCE;
        fs.maxDistance = FIRE_MAX_DISTANCE;
        fs.rollOffFactor = FIRE_ROLLOFF;
        this.addSlots(fs, "fire", false);
      }
      this.app.root.addChild(fire);

      // Reload + draw — short linear curve, fully silent past maxDistance.
      const reload = new pc.Entity(`weapon-mech-${sid}`);
      reload.addComponent("sound");
      const rs = reload.sound;
      if (rs) {
        rs.positional = true;
        rs.distanceModel = pc.DISTANCE_LINEAR;
        rs.refDistance = RELOAD_REF_DISTANCE;
        rs.maxDistance = RELOAD_MAX_DISTANCE;
        rs.rollOffFactor = RELOAD_ROLLOFF;
        this.addSlots(rs, "mech", false);
      }
      this.app.root.addChild(reload);

      emitters = { fire, reload };
      this.peers.set(sid, emitters);
    }
    return emitters;
  }

  // ── Asset loading ───────────────────────────────────────────────────
  /**
   * Add a slot per URL onto `sound`. `group` selects which roles belong on
   * this emitter ("fire" → gunfire only, "mech" → reload + draw); the
   * local 2D emitter takes everything. Fire slots overlap so rapid taps
   * don't cut each other off; mechanical slots don't.
   */
  private addSlots(sound: pc.SoundComponent, group: "fire" | "mech" | "all", isLocal: boolean): void {
    for (const url of ALL_URLS) {
      const role = ROLE_BY_URL.get(url)!;
      const onThisEmitter =
        group === "all" || (group === "fire" ? role === "fire" : role !== "fire");
      if (!onThisEmitter) continue;
      const asset = this.assets.get(url);
      if (!asset) continue;
      sound.addSlot(slotName(url), {
        asset: asset.id,
        volume: isLocal ? localVolume(role) : peerVolume(role),
        loop: false,
        autoPlay: false,
        overlap: role === "fire",
      });
    }
  }

  private loadAssets(): void {
    let remaining = ALL_URLS.length;
    for (const url of ALL_URLS) {
      this.app.assets.loadFromUrl(url, "audio", (err, asset) => {
        if (err || !asset) {
          console.warn("[WeaponSounds] failed to load:", url, err);
          if (--remaining === 0) this.onAssetsReady();
          return;
        }
        this.assets.set(url, asset);
        if (--remaining === 0) this.onAssetsReady();
      });
    }
  }

  private onAssetsReady(): void {
    this.localEmitter.addComponent("sound");
    const s = this.localEmitter.sound;
    if (s) {
      s.positional = false; // 2D — own gun, fixed volume
      this.addSlots(s, "all", true);
    }
    this.ready = true;
    console.log("[WeaponSounds] weapon SFX ready:", Array.from(AUDIBLE_WEAPONS).join(", "));
  }
}
